import React from "react";

import { Box } from "@chakra-ui/react";
import { ProfileCard } from "@ovou-inc/ovou-ui";

import { useElementWidthLayout } from "hooks/useElementWidthLayout";

import { COVER_RATIO, RATE_HEIGHT } from "../../constants";
import type { IProfile } from "types/Profile";
import { addSharpToColorHex, generateMediaPath } from "utils";

import { buildProfileCardProps } from "./buildProfileCardProps";

const CARD_HORIZONTAL_PADDING = 16;

interface ICoverPreviewPanelProps {
  profile: IProfile;
  croppedCover?: string;
}

const CoverPreviewPanel: React.FC<ICoverPreviewPanelProps> = ({
  profile,
  croppedCover,
}) => {
  const { ref: containerRef, width: containerWidth } = useElementWidthLayout();

  const cardWidth = Math.max(0, containerWidth - CARD_HORIZONTAL_PADDING * 2);
  const coverHeight = containerWidth / COVER_RATIO;
  const cardOverlapOffset = Math.ceil(coverHeight / RATE_HEIGHT);
  const hasMeasured = cardWidth > 0;

  const coverSrc =
    croppedCover ||
    (profile.background ? generateMediaPath(profile.background) : undefined);

  const designAccent = profile.design?.foreground_active_color;
  const bg = designAccent ? addSharpToColorHex(designAccent) : "transparent";

  const profileCardProps = buildProfileCardProps(profile, { width: cardWidth });

  return (
    <Box ref={containerRef} w="100%" pb="24px">
      <Box
        w="100%"
        overflow="hidden"
        h={hasMeasured ? `${coverHeight}px` : "0"}
        bg={coverSrc ? undefined : bg}
      >
        {coverSrc && (
          <img
            src={coverSrc}
            alt="cover preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />
        )}
      </Box>
      {hasMeasured && (
        <Box mt={`-${cardOverlapOffset}px`} mx={`${CARD_HORIZONTAL_PADDING}px`}>
          <ProfileCard {...profileCardProps} />
        </Box>
      )}
    </Box>
  );
};

export default CoverPreviewPanel;
